import * as React from 'react';
import { Button } from 'react-bootstrap';
import { GroupBookingUpload, GroupBookingUploadRow } from 'cb-model';
import {
  CSVUploadInstructions,
  fetchApi,
  FileData,
  getRowsFromRawString,
  identifyErrors,
  makeValidator,
  notifyError,
  notifyResponse,
  PageTitle,
  ReportUploadStatus,
  Section,
} from 'gossamer-front-end';
import { CSVRow } from 'gossamer-unified';

export const GroupBookingBulkUpload = (): React.JSX.Element => {
  const [rows, setRows] = React.useState<CSVRow<GroupBookingUploadRow>[] | null>(null);
  const [errors, setErrors] = React.useState<string[][] | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const validator = React.useMemo(() => makeValidator(GroupBookingUpload), []);

  const handleChange = (newValue: string) => {
    setRows([]);
    setErrors([]);
    try {
      const rows = getRowsFromRawString(GroupBookingUpload, newValue);
      setRows(rows);
      setErrors(identifyErrors(rows, validator));
    } catch (e) {
      setRows(null);
      setErrors(null);
      notifyError(e, 'An error occurred while reading the CSV file');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const groupBookingsToUpload = errors
        .map((error) => error.length === 0)
        .map((valid, index) => (valid ? rows[index] : null))
        .filter((row) => !!row);
      const response = await fetchApi('POST', `/main/group-booking-bulk-upload`, {
        bookings: groupBookingsToUpload,
      });
      notifyResponse(response);
      setRows(null);
      setErrors(null);
    } catch (e) {
      notifyError(e, 'An error occurred while uploading group bookings');
    }
    setSubmitting(false);
  };

  return (
    <>
      <PageTitle
        title="Bulk Upload Group Bookings"
        subtitle="Book users onto group events using a CSV file as described below - does NOT add the user to the group"
      />

      <Section>
        <CSVUploadInstructions entity={GroupBookingUpload} />
        <div className="mb20px"></div>
        <div>
          Choose a CSV file:&nbsp;
          <FileData onChange={handleChange} />
        </div>
      </Section>

      <Section>
        {!!errors && (
          <div>
            <ReportUploadStatus errors={errors} />
            <Button onClick={handleSubmit} disabled={!rows || submitting}>
              Save
            </Button>
          </div>
        )}
      </Section>
    </>
  );
};
